/** Analytics → Benchmarks dashboard.
 *
 *  Single scrolling page driven by a shared filter bar (time range,
 *  broker, deal type). Loads one rich payload from
 *  ``/api/analytics/benchmarks`` and slices it into the KPI strip,
 *  daily trend, per-broker leaderboard, day-of-week pattern, deal
 *  stage funnel, broker × day heatmap, and sale-vs-rent split.
 *
 *  Visibility honours the backend — a manager sees their reports only;
 *  director / owner see the whole agency. The broker picker hides
 *  itself when there's only one visible roster (a broker viewing their
 *  own page, etc). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { useAuth } from "@/auth/AuthContext";
import type {
  AnalyticsBenchmarks,
  BenchmarkBrokerRow,
  BenchmarkHeatmapCell,
  DealStage,
  TeamMember,
} from "@/types";
import { DEAL_STAGES, roleLabel } from "@/types";

// ─── Time range ──────────────────────────────────────────────────────

type RangeKey = "this_month" | "last_month" | "7d" | "30d" | "90d" | "ytd" | "custom";

interface RangeState {
  key: RangeKey;
  from: string;
  to: string;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(iso: string, delta: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function firstOfMonth(iso: string): string {
  return iso.slice(0, 8) + "01";
}

function firstOfPrevMonth(iso: string): string {
  const [y, m] = iso.split("-").map(Number);
  const py = m === 1 ? y - 1 : y;
  const pm = m === 1 ? 12 : m - 1;
  return `${py}-${String(pm).padStart(2, "0")}-01`;
}

function lastOfPrevMonth(iso: string): string {
  const first = firstOfMonth(iso);
  return addDays(first, -1);
}

function resolveRange(key: RangeKey, custom: { from: string; to: string }): { from: string; to: string } {
  const today = todayISO();
  if (key === "this_month") return { from: firstOfMonth(today), to: today };
  if (key === "last_month") return { from: firstOfPrevMonth(today), to: lastOfPrevMonth(today) };
  if (key === "7d") return { from: addDays(today, -6), to: today };
  if (key === "30d") return { from: addDays(today, -29), to: today };
  if (key === "90d") return { from: addDays(today, -89), to: today };
  if (key === "ytd") return { from: today.slice(0, 5) + "01-01", to: today };
  return { from: custom.from, to: custom.to };
}

// ─── Format helpers ─────────────────────────────────────────────────

const fmtN = (n: number): string => n.toLocaleString();

function fmtMoney(s: string | number | null | undefined): string {
  if (s == null || s === "") return "—";
  const n = Number(s);
  if (!Number.isFinite(n) || n === 0) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtPct(p: number | null): string {
  if (p == null) return "—";
  return `${Math.round(p * 100)}%`;
}

function fmtShortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const STAGE_LABEL: Record<DealStage, string> = {
  planning: "Planning",
  booked: "Booked",
  decision_pending: "Decision",
  done: "Done",
  lost: "Lost",
};
const STAGE_TONE: Record<DealStage, string> = {
  planning: "bg-blue-500/60",
  booked: "bg-cyan-500/60",
  decision_pending: "bg-amber-500/60",
  done: "bg-emerald-500/70",
  lost: "bg-rose-500/60",
};


// ─── Main page ───────────────────────────────────────────────────────

type SortBy = "placements" | "days_hit" | "deals_done" | "conversion" | "commission";

export function AnalyticsBenchmarks() {
  const { user } = useAuth();
  const todayCached = useMemo(() => todayISO(), []);
  const [range, setRange] = useState<RangeState>({
    key: "this_month",
    from: firstOfMonth(todayCached),
    to: todayCached,
  });
  const [userId, setUserId] = useState<string>("");
  const [dealType, setDealType] = useState<"" | "sale" | "rent">("");
  const [sortBy, setSortBy] = useState<SortBy>("placements");

  const [data, setData] = useState<AnalyticsBenchmarks | null>(null);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams({ since: range.from, until: range.to });
      if (userId) qs.set("user_id", userId);
      if (dealType) qs.set("deal_type", dealType);
      const payload = await api<AnalyticsBenchmarks>(
        `/api/analytics/benchmarks?${qs.toString()}`,
      );
      setData(payload);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load benchmarks");
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, userId, dealType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Roster for the broker picker. Backend already gates /api/team to
    // manager+; brokers viewing the page will fall through to empty
    // (and the picker hides itself).
    api<TeamMember[]>("/api/team")
      .then(setTeam)
      .catch(() => setTeam([]));
  }, []);

  function setChip(key: RangeKey) {
    if (key === "custom") {
      setRange((r) => ({ ...r, key: "custom" }));
      return;
    }
    const { from, to } = resolveRange(key, { from: range.from, to: range.to });
    setRange({ key, from, to });
  }

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title="Benchmarks"
        subtitle="Placement + deal targets across the team — sliced by time, broker, and deal type."
      />

      <FilterBar
        range={range}
        onRangeKey={setChip}
        onCustomFrom={(v) => setRange((r) => ({ ...r, key: "custom", from: v }))}
        onCustomTo={(v) => setRange((r) => ({ ...r, key: "custom", to: v }))}
        userId={userId}
        onUserId={setUserId}
        dealType={dealType}
        onDealType={setDealType}
        team={team}
        viewerId={user?.id}
      />

      {err && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {err}
        </div>
      )}

      <KPIStrip data={data} loading={loading} />

      <DailyTrendCard data={data} loading={loading} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DayOfWeekCard data={data} loading={loading} />
        </div>
        <StageFunnelCard data={data} loading={loading} />
      </div>

      <LeaderboardCard
        data={data}
        loading={loading}
        sortBy={sortBy}
        onSort={setSortBy}
        goalDaily={data?.agency.placement_goal_daily ?? 0}
        goalMonthly={data?.agency.deal_goal_monthly ?? 0}
      />

      <HeatmapCard data={data} loading={loading} />

      <SaleRentSplitCard data={data} loading={loading} />
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────

function FilterBar({
  range,
  onRangeKey,
  onCustomFrom,
  onCustomTo,
  userId,
  onUserId,
  dealType,
  onDealType,
  team,
  viewerId,
}: {
  range: RangeState;
  onRangeKey: (k: RangeKey) => void;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  userId: string;
  onUserId: (v: string) => void;
  dealType: "" | "sale" | "rent";
  onDealType: (v: "" | "sale" | "rent") => void;
  team: TeamMember[];
  viewerId: string | undefined;
}) {
  const chips: Array<{ key: RangeKey; label: string }> = [
    { key: "this_month", label: "This month" },
    { key: "last_month", label: "Last month" },
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "90d", label: "90d" },
    { key: "ytd", label: "YTD" },
    { key: "custom", label: "Custom" },
  ];
  return (
    <div className="card p-3 flex flex-col gap-2.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        {chips.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => onRangeKey(c.key)}
            className={
              "h-8 px-3 rounded-md text-[11.5px] font-medium transition-colors " +
              (range.key === c.key
                ? "bg-accent-500/20 text-accent-100 border border-accent-500/40"
                : "text-slate-400 hover:text-slate-200 border border-transparent hover:bg-white/[0.04]")
            }
          >
            {c.label}
          </button>
        ))}
        {range.key === "custom" && (
          <div className="flex items-center gap-1.5 ml-1">
            <input
              type="date"
              value={range.from}
              onChange={(e) => onCustomFrom(e.target.value)}
              className="h-8 bg-ink-900/60 border border-white/10 rounded-md px-2 text-[11.5px] text-slate-100 tabular-nums focus:outline-none focus:border-white/25"
              max="2999-12-31"
            />
            <span className="text-slate-600 text-xs">→</span>
            <input
              type="date"
              value={range.to}
              onChange={(e) => onCustomTo(e.target.value)}
              className="h-8 bg-ink-900/60 border border-white/10 rounded-md px-2 text-[11.5px] text-slate-100 tabular-nums focus:outline-none focus:border-white/25"
              max="2999-12-31"
            />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {team.length > 1 && (
          <select
            value={userId}
            onChange={(e) => onUserId(e.target.value)}
            className="h-8 bg-ink-900/60 border border-white/10 rounded-md px-2 text-[11.5px] text-slate-200 focus:outline-none focus:border-white/25"
            title="Broker filter"
          >
            <option value="">Everyone ({team.length})</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {(m.full_name || m.email || "—") + (m.id === viewerId ? " (you)" : "")}
              </option>
            ))}
          </select>
        )}
        <div className="inline-flex h-8 items-center bg-ink-900/60 border border-white/10 rounded-md p-0.5">
          {(
            [
              { v: "", label: "All types" },
              { v: "sale", label: "Sale", dot: "bg-indigo-400" },
              { v: "rent", label: "Rent", dot: "bg-teal-400" },
            ] as const
          ).map((o) => {
            const active = dealType === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => onDealType(o.v)}
                className={
                  "inline-flex items-center gap-1.5 h-full px-2.5 rounded text-[11.5px] font-medium transition-colors " +
                  (active
                    ? "bg-white/[0.08] text-slate-100"
                    : "text-slate-400 hover:text-slate-200")
                }
              >
                {"dot" in o && o.dot && (
                  <span className={`w-1.5 h-1.5 rounded-full ${active ? o.dot : "bg-slate-600"}`} />
                )}
                {o.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── KPI strip ───────────────────────────────────────────────────────

function KPIStrip({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  if (loading || !data) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-[88px]" />
        ))}
      </div>
    );
  }
  const a = data.agency;
  const placementsPct = a.placements_expected > 0
    ? a.placements_total / a.placements_expected
    : null;
  const daysHitPct = a.broker_days > 0
    ? a.days_hit_goal / a.broker_days
    : null;
  const dealsClosed = a.deals_done + a.deals_lost;
  const topBroker = data.by_broker[0];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KPI
        label="Placements"
        primary={fmtN(a.placements_total)}
        secondary={`of ${fmtN(a.placements_expected)} expected`}
        progress={placementsPct}
        accentMet="text-emerald-300"
      />
      <KPI
        label="Days hit goal"
        primary={`${a.days_hit_goal}`}
        secondary={`of ${a.broker_days} broker-days`}
        progress={daysHitPct}
        accentMet="text-emerald-300"
      />
      <KPI
        label="Deals closed"
        primary={`${dealsClosed}`}
        secondary={
          dealsClosed === 0
            ? `goal ${a.deal_goal_monthly}/mo · ${data.visible_broker_count} broker${data.visible_broker_count === 1 ? "" : "s"}`
            : `${a.deals_done} done · ${a.deals_lost} lost · ${fmtPct(a.conversion_rate)} won`
        }
        progress={null}
        accentMet="text-emerald-300"
        extra={fmtMoney(a.commission_value)}
        extraLabel={data.commission_fx_complete ? "commission" : "commission ≈"}
      />
      <KPI
        label="Top performer"
        primary={topBroker ? topBroker.full_name || topBroker.email || "—" : "—"}
        secondary={
          topBroker
            ? `${fmtN(topBroker.placements)} placements · ${topBroker.days_hit_goal} days hit`
            : "no activity"
        }
        progress={null}
      />
    </div>
  );
}

function KPI({
  label,
  primary,
  secondary,
  progress,
  accentMet,
  extra,
  extraLabel,
}: {
  label: string;
  primary: string;
  secondary?: string;
  progress: number | null;
  accentMet?: string;
  extra?: string;
  extraLabel?: string;
}) {
  const pct = progress != null ? Math.round(Math.min(1, progress) * 100) : null;
  const met = progress != null && progress >= 1;
  const close = progress != null && !met && progress >= 0.8;
  const barTone = met
    ? "bg-emerald-500"
    : close
      ? "bg-accent-500"
      : "bg-slate-500/60";
  const primaryTone = met
    ? accentMet ?? "text-emerald-300"
    : close
      ? "text-accent-200"
      : "text-slate-100";
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </span>
        {pct != null && (
          <span className="text-[10px] text-slate-500 tabular-nums">{pct}%</span>
        )}
      </div>
      <div className={`text-2xl font-semibold leading-tight tabular-nums truncate ${primaryTone}`}>
        {primary}
      </div>
      {secondary && (
        <div className="text-[11px] text-slate-500 mt-0.5">{secondary}</div>
      )}
      {progress != null && (
        <div className="h-1.5 bg-white/[0.06] rounded-sm overflow-hidden mt-2">
          <div
            className={`h-full ${barTone} transition-all`}
            style={{ width: `${Math.max(2, pct ?? 0)}%` }}
          />
        </div>
      )}
      {extra && (
        <div className="text-[11px] text-slate-400 mt-2 tabular-nums">
          <span className="text-emerald-200">{extra}</span>{" "}
          <span className="text-slate-600">{extraLabel}</span>
        </div>
      )}
    </div>
  );
}

// ─── Daily trend ─────────────────────────────────────────────────────

function DailyTrendCard({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  if (loading || !data) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Daily placements
        </div>
        <Skeleton className="h-[180px]" />
      </div>
    );
  }
  const goal = data.agency.placement_goal_daily;
  const expectedPerDay = goal * data.visible_broker_count;
  const max = Math.max(
    expectedPerDay,
    ...data.daily.map((d) => d.placements),
    1,
  );
  const yMax = Math.max(max, Math.round(expectedPerDay * 1.2));

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Daily placements
          </span>
          <span className="text-[11px] text-slate-500">
            · agency-wide
          </span>
        </div>
        <div className="text-[10.5px] text-slate-500 tabular-nums">
          dashed line ={" "}
          <span className="text-rose-300/80">
            {expectedPerDay}/day
          </span>{" "}
          ({goal} × {data.visible_broker_count})
        </div>
      </div>
      <div className="relative h-[180px] flex items-end gap-[2px]">
        <div
          className="absolute left-0 right-0 border-t border-dashed border-rose-400/50 pointer-events-none"
          style={{ bottom: `${(expectedPerDay / yMax) * 100}%` }}
          aria-hidden
        />
        {data.daily.map((d) => {
          const h = (d.placements / yMax) * 100;
          const hitFrac = data.visible_broker_count > 0
            ? d.brokers_hit_goal / data.visible_broker_count
            : 0;
          const tone = hitFrac >= 0.6
            ? "bg-emerald-500/70 hover:bg-emerald-400"
            : d.placements > 0
              ? "bg-accent-500/55 hover:bg-accent-400"
              : "bg-white/[0.06]";
          return (
            <div
              key={d.date}
              className="flex-1 h-full flex flex-col justify-end relative group/bar"
              title={
                `${fmtShortDate(d.date)} · ${d.placements} placements\n` +
                `${d.brokers_hit_goal}/${data.visible_broker_count} brokers hit goal · ${d.active_brokers} active`
              }
            >
              <div
                className={`w-full rounded-t-[2px] transition-colors ${tone}`}
                style={{ height: `${Math.max(h, d.placements > 0 ? 3 : 1)}%` }}
              />
              <div className="opacity-0 group-hover/bar:opacity-100 transition pointer-events-none absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-900/95 border border-white/10 text-slate-200 whitespace-nowrap z-10">
                {fmtShortDate(d.date)} · {d.placements}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-1.5 text-[10px] text-slate-600 tabular-nums">
        <span>{fmtShortDate(data.window_start)}</span>
        <span>{data.days_in_window} days · {data.visible_broker_count} brokers</span>
        <span>{fmtShortDate(data.window_end)}</span>
      </div>
    </div>
  );
}

// ─── Day of week ─────────────────────────────────────────────────────

function DayOfWeekCard({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  if (loading || !data) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          By day of week
        </div>
        <Skeleton className="h-[120px]" />
      </div>
    );
  }
  const max = Math.max(...data.by_dow.map((d) => d.placements), 1);
  const best = data.by_dow.reduce(
    (acc, d) => (d.placements > acc.placements ? d : acc),
    data.by_dow[0],
  );
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          By day of week
        </span>
        {best && (
          <span className="text-[10.5px] text-slate-500">
            Best:{" "}
            <span className="text-emerald-300 font-semibold">{best.label}</span>{" "}
            ({fmtN(best.placements)})
          </span>
        )}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {data.by_dow.map((d) => {
          const h = (d.placements / max) * 100;
          const isBest = d.dow === best?.dow;
          return (
            <div
              key={d.dow}
              className="flex flex-col items-center gap-1.5"
              title={`${d.label} · ${d.placements} placements · ${d.deals_closed} closed deals`}
            >
              <div className="w-full h-[80px] flex items-end">
                <div
                  className={`w-full rounded-t-sm transition-colors ${
                    isBest ? "bg-emerald-500/80" : "bg-accent-500/55"
                  }`}
                  style={{ height: `${Math.max(h, d.placements > 0 ? 4 : 1)}%` }}
                />
              </div>
              <div className="text-[10px] text-slate-400 tabular-nums text-center">
                <div className={isBest ? "font-semibold text-emerald-300" : ""}>
                  {d.label}
                </div>
                <div className="text-slate-500">{d.placements}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Stage funnel ───────────────────────────────────────────────────

function StageFunnelCard({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  if (loading || !data) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Funnel snapshot
        </div>
        <Skeleton className="h-[120px]" />
      </div>
    );
  }
  const max = Math.max(...data.deal_stages.map((s) => s.count), 1);
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
        Funnel snapshot
        <span className="ml-1.5 text-slate-600 font-normal">· live</span>
      </div>
      <div className="space-y-1.5">
        {DEAL_STAGES.map((stage) => {
          const row = data.deal_stages.find((s) => s.stage === stage);
          const count = row?.count ?? 0;
          const pct = (count / max) * 100;
          return (
            <div key={stage} className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400 w-20 truncate">
                {STAGE_LABEL[stage]}
              </span>
              <div className="flex-1 h-3 bg-white/[0.04] rounded-sm overflow-hidden">
                <div
                  className={`h-full ${STAGE_TONE[stage]}`}
                  style={{ width: `${Math.max(2, pct)}%` }}
                />
              </div>
              <span className="text-[11px] text-slate-300 tabular-nums w-8 text-right">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Leaderboard ─────────────────────────────────────────────────────

function LeaderboardCard({
  data,
  loading,
  sortBy,
  onSort,
  goalDaily,
  goalMonthly,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
  sortBy: SortBy;
  onSort: (v: SortBy) => void;
  goalDaily: number;
  goalMonthly: number;
}) {
  const rows = useMemo<BenchmarkBrokerRow[]>(() => {
    if (!data) return [];
    const r = [...data.by_broker];
    r.sort((a, b) => {
      switch (sortBy) {
        case "days_hit":
          return b.days_hit_goal - a.days_hit_goal;
        case "deals_done":
          return b.deals_done - a.deals_done;
        case "conversion":
          return (b.conversion_rate ?? -1) - (a.conversion_rate ?? -1);
        case "commission":
          return Number(b.commission_value) - Number(a.commission_value);
        case "placements":
        default:
          return b.placements - a.placements;
      }
    });
    return r;
  }, [data, sortBy]);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-white/5 flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Leaderboard
          </span>
          {data && (
            <span className="text-[11px] text-slate-500">
              · {data.visible_broker_count} broker{data.visible_broker_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500">
            Sort by
          </span>
          <select
            value={sortBy}
            onChange={(e) => onSort(e.target.value as SortBy)}
            className="h-7 bg-ink-900/60 border border-white/10 rounded-md px-2 text-[11px] text-slate-200 focus:outline-none focus:border-white/25"
          >
            <option value="placements">Placements</option>
            <option value="days_hit">Days hit goal</option>
            <option value="deals_done">Deals done</option>
            <option value="conversion">Conversion %</option>
            <option value="commission">Commission $</option>
          </select>
        </div>
      </div>
      {loading || !data ? (
        <div className="p-4">
          <Skeleton className="h-32" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px] text-slate-300 tabular-nums">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.015]">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Broker</th>
                <th className="text-right px-3 py-2 font-medium">Placements</th>
                <th className="text-right px-3 py-2 font-medium">Days hit</th>
                <th className="text-right px-3 py-2 font-medium">Best day</th>
                <th className="text-right px-3 py-2 font-medium">Done</th>
                <th className="text-right px-3 py-2 font-medium">Lost</th>
                <th className="text-right px-3 py-2 font-medium">Conv</th>
                <th className="text-right px-3 py-2 font-medium">Sale / Rent</th>
                <th className="text-right px-3 py-2 font-medium pr-4">Commission</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-slate-600 text-[11px]">
                    No data for the selected window.
                  </td>
                </tr>
              )}
              {rows.map((b) => {
                const pPct = goalDaily > 0 && b.days_active > 0
                  ? b.placements / (goalDaily * b.days_active)
                  : null;
                const closed = b.deals_done + b.deals_lost;
                const closedPct = goalMonthly > 0
                  ? b.deals_done / goalMonthly
                  : null;
                return (
                  <tr
                    key={b.user_id}
                    className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Avatar
                          size="xs"
                          name={b.full_name}
                          email={b.email}
                          url={b.avatar_url}
                        />
                        <div className="min-w-0">
                          <div className="text-slate-200 truncate">
                            {b.full_name || b.email || "—"}
                          </div>
                          <div className="text-[10px] text-slate-600">
                            {roleLabel(b.role)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right px-3 py-2">
                      <div className="text-slate-200">{fmtN(b.placements)}</div>
                      {pPct != null && (
                        <div className="text-[10px] text-slate-600">
                          {Math.round(pPct * 100)}% of goal
                        </div>
                      )}
                    </td>
                    <td className="text-right px-3 py-2">
                      <span className="text-emerald-300">{b.days_hit_goal}</span>
                      <span className="text-slate-600">/{b.days_active}</span>
                    </td>
                    <td className="text-right px-3 py-2">
                      {b.best_day_count > 0 ? (
                        <>
                          <div className="text-slate-200">{b.best_day_count}</div>
                          <div className="text-[10px] text-slate-600">
                            {b.best_day_date ? fmtShortDate(b.best_day_date) : ""}
                          </div>
                        </>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="text-right px-3 py-2">
                      <span className="text-emerald-300">{b.deals_done}</span>
                      {closedPct != null && (
                        <div className="text-[10px] text-slate-600">
                          {Math.round(closedPct * 100)}% of goal
                        </div>
                      )}
                    </td>
                    <td className="text-right px-3 py-2 text-rose-300/80">
                      {b.deals_lost}
                    </td>
                    <td className="text-right px-3 py-2">
                      {closed > 0 ? fmtPct(b.conversion_rate) : <span className="text-slate-600">—</span>}
                    </td>
                    <td className="text-right px-3 py-2">
                      <span className="text-indigo-300">{b.sale_count}</span>
                      <span className="text-slate-600"> / </span>
                      <span className="text-teal-300">{b.rent_count}</span>
                    </td>
                    <td className="text-right px-3 py-2 pr-4 text-emerald-200">
                      {fmtMoney(b.commission_value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Heatmap ─────────────────────────────────────────────────────────

function HeatmapCard({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  const map = useMemo(() => {
    const m = new Map<string, BenchmarkHeatmapCell>();
    if (!data) return m;
    for (const c of data.heatmap) m.set(`${c.user_id}|${c.date}`, c);
    return m;
  }, [data]);

  if (loading || !data) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Activity heatmap
        </div>
        <Skeleton className="h-[140px]" />
      </div>
    );
  }
  const brokers = data.by_broker;
  const days = data.daily.map((d) => d.date);
  const goal = data.agency.placement_goal_daily;

  if (brokers.length === 0 || days.length === 0) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Activity heatmap
        </div>
        <div className="text-[11px] text-slate-600 text-center py-4 border border-dashed border-white/8 rounded">
          No activity to render.
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Activity heatmap
        </span>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <Legend tone="bg-white/[0.06]" label="0" />
          <Legend tone="bg-accent-500/35" label="some" />
          <Legend tone="bg-accent-500/70" label={`<${goal}`} />
          <Legend tone="bg-emerald-500/70" label={`≥${goal}`} />
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="flex">
            <div className="w-32 flex-shrink-0" />
            <div className="flex gap-[2px]">
              {days.map((d, i) => {
                const showLabel = i === 0 || i === days.length - 1 || i % 5 === 0;
                return (
                  <div
                    key={d}
                    className="w-3.5 text-[8.5px] text-slate-600 tabular-nums text-center"
                  >
                    {showLabel ? new Date(`${d}T00:00:00`).getDate() : ""}
                  </div>
                );
              })}
            </div>
          </div>
          {brokers.map((b) => (
            <div key={b.user_id} className="flex items-center mt-[2px]">
              <div className="w-32 pr-2 flex items-center gap-1.5 flex-shrink-0">
                <Avatar size="xs" name={b.full_name} email={b.email} url={b.avatar_url} />
                <span className="text-[10.5px] text-slate-300 truncate">
                  {(b.full_name || b.email || "—").split(" ")[0]}
                </span>
              </div>
              <div className="flex gap-[2px]">
                {days.map((d) => {
                  const c = map.get(`${b.user_id}|${d}`);
                  const count = c?.placements ?? 0;
                  const hit = c?.hit_goal ?? false;
                  const tone = count === 0
                    ? "bg-white/[0.06]"
                    : hit
                      ? "bg-emerald-500/70"
                      : count >= goal / 2
                        ? "bg-accent-500/70"
                        : "bg-accent-500/35";
                  return (
                    <div
                      key={d}
                      className={`w-3.5 h-3.5 rounded-[2px] ${tone}`}
                      title={`${b.full_name || b.email} · ${fmtShortDate(d)} · ${count}/${goal}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Legend({ tone, label }: { tone: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-2.5 h-2.5 rounded-[2px] ${tone}`} />
      {label}
    </span>
  );
}

// ─── Sale vs Rent split ─────────────────────────────────────────────

function SaleRentSplitCard({
  data,
  loading,
}: {
  data: AnalyticsBenchmarks | null;
  loading: boolean;
}) {
  if (loading || !data) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Sale vs rent
        </div>
        <Skeleton className="h-[100px]" />
      </div>
    );
  }
  const rows = data.by_broker.filter((b) => b.sale_count + b.rent_count > 0);
  if (rows.length === 0) {
    return (
      <div className="card p-4">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
          Sale vs rent
        </div>
        <div className="text-[11px] text-slate-600 text-center py-4 border border-dashed border-white/8 rounded">
          No closed deals to split.
        </div>
      </div>
    );
  }
  const max = Math.max(
    ...rows.map((b) => b.sale_count + b.rent_count),
    1,
  );
  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          Sale vs rent · per broker
        </span>
        <span className="text-[10.5px] text-slate-500 tabular-nums">
          agency:{" "}
          <span className="text-indigo-300">{data.agency.sale_count} sale</span>{" "}
          ·{" "}
          <span className="text-teal-300">{data.agency.rent_count} rent</span>
        </span>
      </div>
      <div className="space-y-1.5">
        {rows.map((b) => {
          const total = b.sale_count + b.rent_count;
          const widthPct = (total / max) * 100;
          const salePct = total > 0 ? (b.sale_count / total) * 100 : 0;
          return (
            <div key={b.user_id} className="flex items-center gap-2">
              <span className="text-[11px] text-slate-300 w-28 truncate">
                {(b.full_name || b.email || "—").split(" ")[0]}
              </span>
              <div
                className="flex h-3 rounded-sm overflow-hidden"
                style={{ width: `${widthPct}%`, minWidth: "12px" }}
                title={`${total} closed · ${b.sale_count} sale · ${b.rent_count} rent`}
              >
                <div
                  className="bg-indigo-500/70 h-full"
                  style={{ width: `${salePct}%` }}
                />
                <div className="bg-teal-500/70 h-full flex-1" />
              </div>
              <span className="text-[10.5px] text-slate-500 tabular-nums">
                {b.sale_count}
                <span className="text-slate-700">/</span>
                {b.rent_count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
